'use client';

import React from 'react';
import { useTranslation } from 'react-i18next';
import { X as XIcon } from 'lucide-react';

import { Icon, Modal } from '@nutralab/ui';

import styles from './PromoModal.module.scss';

import { ModalProps } from '@/shared/types';

export interface PromoModalData {
    image: string;
    link: string;
    onDismiss?: () => void;
}

type PromoModalProps = ModalProps<PromoModalData>;

function isSameOriginLink(link: string): boolean {
    try {
        return new URL(link, window.location.href).origin === window.location.origin;
    } catch {
        return false;
    }
}

/** A CMS promo popup: an image with a link. */
export function PromoModal({ onClose, zIndex, data }: PromoModalProps): React.ReactElement {
    const { t } = useTranslation();
    const { image, link, onDismiss } = data;

    const handleClose = (): void => {
        onClose();
        onDismiss?.();
    };

    const rel = isSameOriginLink(link) ? undefined : 'noopener noreferrer';

    return (
        <Modal open onClose={handleClose} zIndex={zIndex} title={t('promo.modal.title')}>
            <div
                role="presentation"
                className={styles.popup}
                onMouseDown={(e) => {
                    if (e.target === e.currentTarget) {
                        handleClose();
                    }
                }}
            >
                {/* eslint-disable-next-line react/jsx-no-target-blank */}
                <a href={link} target="_blank" rel={rel} className={styles['image-link']} onClick={handleClose}>
                    <img src={image} alt={t('promo.image-alt')} className={styles.image} />
                </a>

                <button
                    type="button"
                    aria-label={t('promo.modal.close')}
                    className={styles['close-button']}
                    onClick={handleClose}
                >
                    <Icon icon={XIcon} size="xl" />
                </button>
            </div>
        </Modal>
    );
}
